// 壞 JSON 紀錄「重新批改」的收斂狀態機(kanban 1000412 review 必修 1/建議 2)。
//
// 背景:壞 JSON 的 classrecord 按「重新批改」後,requeue 的那筆因跨來源去重
// (_id 已在 classRecordIds)永遠不會出現在 pendingRows,「批改中」只能靠元件
// local 的 regrading 集合自行呈現,而集合何時把 id 移掉(收斂)全靠輪詢回來的
// 兩份資料推斷。原本只看「解析成功/已刪除/batch 終端 failed|prohibited」三個
// 出口,漏了「worker 重跑後又寫回壞 JSON」:那時 batch 狀態是 finish、
// classrecord 仍解析失敗,三個出口全不成立 → 該列永久「批改中」、輪詢永不停。
//
// 這裡把「下一輪 regrading 狀態」的計算抽成純函式,每個 id 追蹤階段:
//   requeued:reopen PUT 成功,還沒在輪詢資料裡看過該筆真的回到佇列
//   running :輪詢已看過該筆 open/running(classifyBatchStatus → "grading")
// 出口(從集合移除,該列退回「批改失敗」或直接顯示修好的紀錄):
//   - id 不在 brokenIds:重批覆寫成功(解析已過)、被刪除、或切班 → 移除
//   - batch 狀態 failed/prohibited:終端,重跑也不會有結果 → 移除
//   - batch 狀態 finish 且「已看過 running」:worker 重跑完、classrecord 卻仍是
//     壞的(仍在 brokenIds)→ 視為「重批後仍壞」的終端 → 移除
//   - 保底:任何階段存活超過 maxTicks 輪 → 強制移除,避免漏算把列鎖死
// 「先看過 open/running 才接受 finish 為終端」是為了擋同一輪 tick 裡
// batch-results 與 classrecord 平行請求的交錯:重批修好時後端順序是
// 「寫回 classrecord → 才標 finish」,但 finish 可能先到、覆寫後的 classrecord
// 慢一拍,requeued 階段就把 finish 當終端會把剛修好的列誤判成「仍壞」。
// requeued 階段同時也不吃殘留的 finish 舊快照(reopen 前該筆本來就是 finish)。
//
// 在途保護(建議 2):reopen PUT 還在途時,輪詢落地的舊快照(可能還載著
// failed/finish 等 reopen 前狀態)不得動剛樂觀加入的 id —— inFlightIds 內的
// id 一律原樣保留、連 ticks 都不動;PUT 結束(成功或失敗)呼叫端才把 id
// 移出在途集合。單把請求序號提前擋不住 await 期間起跑的新輪詢,集合才擋得住。
//
// 不碰 DOM 也不打 API — 方便 vitest(node)直測。

import type { BatchGradingUiStatus } from "./batchGradingStatus";

export type RegradingPhase = "requeued" | "running";

export interface RegradingEntry {
  phase: RegradingPhase;
  /** 收斂計算已跑過的輪數(保底計數;在途中不累計) */
  ticks: number;
}

/** 元件 state 用 Map:UI 只用 .has()/.size,與原本 Set 寫法相容 */
export type RegradingState = Map<string, RegradingEntry>;

export interface RegradingInputs {
  /** 仍解析失敗的 classrecord _id 集合(整份 classRecordsData 重判) */
  brokenIds: ReadonlySet<string>;
  /** batchResultID → UI 狀態(classifyBatchStatus 後);缺席=這輪沒看到該筆 */
  batchStatuses: ReadonlyMap<string, BatchGradingUiStatus>;
  /** reopen PUT 在途中的 id — 一律跳過不動(見檔頭「在途保護」) */
  inFlightIds: ReadonlySet<string>;
  /** 保底輪數上限,預設 DEFAULT_REGRADING_MAX_TICKS */
  maxTicks?: number;
}

/**
 * 保底輪數。收斂計算由輪詢的兩個回應(batch-results / classrecord)觸發,
 * 10 秒一輪、每輪約跑 1〜2 次,36 次約 3〜6 分鐘 —— 正常重批(單篇數十秒)
 * 早就走到終端,只有漏算才會撞到這條。
 */
export const DEFAULT_REGRADING_MAX_TICKS = 36;

/**
 * 算出下一輪 regrading 狀態(純函式、immutable)。
 * 沒有任何變化時回傳 prev 原參考(React setState 靠同參考略過 re-render)。
 */
export const computeNextRegradingState = (
  prev: ReadonlyMap<string, RegradingEntry>,
  inputs: RegradingInputs
): RegradingState => {
  const {
    brokenIds,
    batchStatuses,
    inFlightIds,
    maxTicks = DEFAULT_REGRADING_MAX_TICKS,
  } = inputs;
  if (prev.size === 0) return prev as RegradingState;

  let changed = false;
  const next: RegradingState = new Map();

  for (const [id, entry] of prev) {
    // 在途保護:PUT 沒結束前,任何快照都可能是 reopen 前的舊資料,原樣保留
    if (inFlightIds.has(id)) {
      next.set(id, entry);
      continue;
    }

    // 出口 1:已不是壞資料(重批覆寫成功/被刪除/切班)→ 移除
    if (!brokenIds.has(id)) {
      changed = true;
      continue;
    }

    const status = batchStatuses.get(id);

    // 出口 2:終端狀態 failed/prohibited → 移除,退回「批改失敗」可再按
    if (status === "failed" || status === "prohibited") {
      changed = true;
      continue;
    }

    // 出口 3:已看過 running 後才接受 finish 為「重批後仍壞」的終端(見檔頭)
    if (status === "done" && entry.phase === "running") {
      changed = true;
      continue;
    }

    const phase: RegradingPhase =
      status === "grading" ? "running" : entry.phase;
    const ticks = entry.ticks + 1;

    // 出口 4:保底 —— 存活超過上限一律強制退回,不讓任何漏算把列鎖死
    if (ticks >= maxTicks) {
      changed = true;
      continue;
    }

    if (phase !== entry.phase || ticks !== entry.ticks) changed = true;
    next.set(id, { phase, ticks });
  }

  return changed ? next : (prev as RegradingState);
};

/** reopen PUT 送出時的樂觀加入:phase=requeued、ticks 歸零(重按也重算保底) */
export const startRegrading = (
  prev: ReadonlyMap<string, RegradingEntry>,
  id: string
): RegradingState => {
  const next: RegradingState = new Map(prev);
  next.set(id, { phase: "requeued", ticks: 0 });
  return next;
};

/** reopen PUT 失敗時的退回:移除該 id,該列回到「批改失敗」可再按 */
export const cancelRegrading = (
  prev: ReadonlyMap<string, RegradingEntry>,
  id: string
): RegradingState => {
  if (!prev.has(id)) return prev as RegradingState;
  const next: RegradingState = new Map(prev);
  next.delete(id);
  return next;
};
